<script setup lang="ts">
import { ref, computed } from 'vue';
import { every, omit, isEmpty } from 'lodash';
import { useComponentComposable } from '@/components/useComponentComposable';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import Accordion from 'primevue/accordion';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import AccordionPanel from 'primevue/accordionpanel';
import Dialog from 'primevue/dialog';
import LongParagraphModal from '../modal/utils/LongParagraphModal.vue';
import ShowImageByScreen from '@/components/ShowImageByScreen.vue';

interface Props {
  value: any;
  maxContentCharacters?: number;
  showTerm?: boolean;
}
const props = defineProps<Props>();

const { buttonIconExpand, buttonIconCollapse } = usePromoComposable();
const { renderHTMLContent } = useComponentComposable();
const position = ref('center');
const showTermAndConditions = ref(false);
const activePanel = ref();

const detailInfo = computed(() => {
  return props.value;
});

const isEmptyDetail = computed(() => {
  return every(omit(props.value, ['sk']), isEmpty);
});
const openResponsive = (pos?) => {
  position.value = pos;
  showTermAndConditions.value = true;
};
</script>
<template>
  <Accordion v-if="!isEmptyDetail" v-model:value="activePanel" class="accordion-promo acvShadow" :expandIcon="'fa-light fa-3x fa-circle-plus text-primary'" :collapseIcon="'fa-light fa-3x fa-circle-minus text-primary'">
    <AccordionPanel value="0">
      <AccordionHeader>
        <ShowImageByScreen alt="image-render" class="image" :images="detailInfo.renderImage"></ShowImageByScreen>
        <div class="accordion-title flex-grow-1 flex flex-col gap-3 min-h-60 bg-white">
          <renderHTMLContent class="accordion-title-inner" :data="detailInfo.title"></renderHTMLContent>
          <renderHTMLContent v-if="detailInfo.subTitle" class="font-normal" :data="detailInfo.subTitle" htmlElement="p"></renderHTMLContent>
        </div>
        <div class="absolute right-0 px-3 flex align-items-center h-full expand-icon">
          <img class="button-icon" v-if="activePanel != 0" :src="buttonIconExpand" alt="Expand" />
          <img class="button-icon" v-else :src="buttonIconCollapse" alt="Collapse" />
        </div>
      </AccordionHeader>
      <AccordionContent>
        <LongParagraphModal class-component="break-word text-semiblack" :text="detailInfo.description" :max-characters="maxContentCharacters || 1000" />
        <div v-if="showTerm" class="text-12px text-semiblack">
          <div class="mt-2">{{ $t('promos.labels.term') }}</div>
          <renderHTMLContent :data="detailInfo.terms" />
        </div>
        <div v-else>
          <a v-if="detailInfo.terms" class="tAndC cursor-pointer" @click="openResponsive()">{{ $t('promos.labels.term') }}</a>
        </div>
        <Dialog :header="$t('common.termcondition')" v-model:visible="showTermAndConditions" :position="position" :modal="true" class="tcs accordion-tearm-modal">
          <renderHTMLContent :data="detailInfo.terms" />
        </Dialog>
      </AccordionContent>
    </AccordionPanel>
  </Accordion>
</template>

<style lang="scss" scoped>
.acvShadow :deep() {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  border: none !important;
  border-radius: 5px;
  .p-accordion-header {
    background: $col-white;
    border-radius: 5px;
  }
  .p-accordionheader {
    padding: 0;
    display: flex;
    align-items: center;
    color: $col-black;
    border-radius: 5px;
  }

  .p-accordionheader:hover {
    background: $col-white !important;
  }

  .p-accordion-toggle-icon {
    order: 2;
  }
  .text-12px {
    font-size: 12px;
  }
  .text-semiblack {
    color: $col-black !important;
  }
}

@media screen and (max-width: 767px) {
  .image {
    width: 100%;
    border-radius: 5px 5px 0 0;
  }
  .expand-icon {
    bottom: 0;
    border-radius: 0 0 5px 0;
    height: calc(100% - 105px) !important;
  }
  .acvShadow :deep() {
    .p-accordionheader {
      flex-direction: column;
    }
  }
}
</style>
